import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { haversineDistanceMiles } from '@limo/shared/pricing'
import type { Tables } from '@limo/database'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Plus, Pencil, Trash2, MapPin, ArrowLeftRight, Plane } from 'lucide-react'

import { ZipZoneDialog } from '@/components/zip-flat-rate/ZipZoneDialog'
import { ZipRuleDialog, type ZipRuleWithZone } from '@/components/zip-flat-rate/ZipRuleDialog'
import { ZipRateImportDialog } from '@/components/zip-flat-rate/ZipRateImportDialog'
import { ZipHubInput, type ZipHubSelection } from '@/components/zip-flat-rate/ZipHubInput'

type ZipFlatRateZone = Tables<'zip_flat_rate_zones'>
type ZipFlatRateHub = Tables<'zip_flat_rate_hubs'>

const DUPLICATE_HUB_THRESHOLD_MILES = 0.5
const DEFAULT_HUB_RADIUS_MILES = 2.0

export function ZipFlatRatePricingPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const activeAccount = isImpersonating ? impersonatedAccount : account

  const [zoneDialogOpen, setZoneDialogOpen] = useState(false)
  const [editingZone, setEditingZone] = useState<ZipFlatRateZone | null>(null)
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<ZipRuleWithZone | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [deleteZoneOpen, setDeleteZoneOpen] = useState(false)
  const [deletingZone, setDeletingZone] = useState<ZipFlatRateZone | null>(null)
  const [deleteRuleOpen, setDeleteRuleOpen] = useState(false)
  const [deletingRule, setDeletingRule] = useState<ZipRuleWithZone | null>(null)

  const { data: hubs, isLoading: hubsLoading } = useQuery({
    queryKey: ['zip-flat-rate-hubs', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []
      const { data, error } = await supabase
        .from('zip_flat_rate_hubs')
        .select('*')
        .eq('account_id', activeAccount.id)
        .order('name', { ascending: true })
      if (error) throw error
      return (data || []) as ZipFlatRateHub[]
    },
    enabled: !!activeAccount?.id,
  })

  const { data: zones, isLoading: zonesLoading } = useQuery({
    queryKey: ['zip-flat-rate-zones', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []
      const { data, error } = await supabase
        .from('zip_flat_rate_zones')
        .select('*')
        .eq('account_id', activeAccount.id)
        .order('name', { ascending: true })
      if (error) throw error
      return data || []
    },
    enabled: !!activeAccount?.id,
  })

  const { data: rules, isLoading: rulesLoading } = useQuery({
    queryKey: ['zip-flat-rate-rules', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []
      const { data, error } = await supabase
        .from('zip_flat_rate_rules')
        .select(`*, zip_zone:zip_flat_rate_zones!zip_zone_id(*)`)
        .eq('account_id', activeAccount.id)
        .order('priority', { ascending: true })
      if (error) throw error
      return (data || []) as ZipRuleWithZone[]
    },
    enabled: !!activeAccount?.id,
  })

  const { data: vehicles } = useQuery({
    queryKey: ['vehicles', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('account_id', activeAccount.id)
        .eq('is_active', true)
        .order('display_order', { ascending: true })
      if (error) throw error
      return data || []
    },
    enabled: !!activeAccount?.id,
  })

  const addHubMutation = useMutation({
    mutationFn: async (hub: ZipHubSelection) => {
      if (!activeAccount?.id) throw new Error('No account found')
      const isDuplicate = (hubs ?? []).some(
        (h) => haversineDistanceMiles(h.lat, h.lng, hub.lat, hub.lng) < DUPLICATE_HUB_THRESHOLD_MILES
      )
      if (isDuplicate) throw new Error('This hub has already been added')
      const { error } = await supabase.from('zip_flat_rate_hubs').insert({
        account_id: activeAccount.id,
        name: hub.name,
        address: hub.address,
        lat: hub.lat,
        lng: hub.lng,
        radius_miles: DEFAULT_HUB_RADIUS_MILES,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zip-flat-rate-hubs', activeAccount?.id] })
      toast({ title: 'Hub added', description: 'The hub has been added.' })
    },
    onError: (error) => toast({ title: 'Error', description: error.message, variant: 'destructive' }),
  })

  const updateHubRadiusMutation = useMutation({
    mutationFn: async ({ id, radius }: { id: string; radius: number }) => {
      const { error } = await supabase
        .from('zip_flat_rate_hubs')
        .update({ radius_miles: radius })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['zip-flat-rate-hubs', activeAccount?.id] }),
    onError: (error) => toast({ title: 'Error', description: error.message, variant: 'destructive' }),
  })

  const removeHubMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('zip_flat_rate_hubs').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zip-flat-rate-hubs', activeAccount?.id] })
      toast({ title: 'Hub removed', description: 'The hub has been removed.' })
    },
    onError: (error) => toast({ title: 'Error', description: error.message, variant: 'destructive' }),
  })

  const deleteZoneMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('zip_flat_rate_zones').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zip-flat-rate-zones', activeAccount?.id] })
      queryClient.invalidateQueries({ queryKey: ['zip-flat-rate-rules', activeAccount?.id] })
      toast({ title: 'Zone deleted', description: 'The ZIP zone and its rules were removed.' })
      setDeleteZoneOpen(false)
      setDeletingZone(null)
    },
    onError: (error) => toast({ title: 'Error', description: error.message, variant: 'destructive' }),
  })

  const deleteRuleMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('zip_flat_rate_rules').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zip-flat-rate-rules', activeAccount?.id] })
      toast({ title: 'Rule deleted', description: 'The ZIP flat rate rule was removed.' })
      setDeleteRuleOpen(false)
      setDeletingRule(null)
    },
    onError: (error) => toast({ title: 'Error', description: error.message, variant: 'destructive' }),
  })

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price)

  const getVehicleCountText = (vehicleIds: string[] | null) => {
    if (!vehicleIds) return 'All Vehicles'
    if (vehicleIds.length === 1) return '1 Vehicle'
    return `${vehicleIds.length} Vehicles`
  }

  const isLoading = hubsLoading || zonesLoading || rulesLoading
  const hubList = hubs ?? []
  const noHubs = hubList.length === 0

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-40 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Hubs */}
      <Card>
        <CardHeader>
          <CardTitle>Hubs</CardTitle>
          <CardDescription>
            ZIP flat rates apply between a hub (e.g. the airport) and a ZIP zone, in either
            direction. A trip end counts as the hub when it falls within the hub's radius.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ZipHubInput onSelect={(hub) => addHubMutation.mutate(hub)} disabled={addHubMutation.isPending} />

          {hubList.length > 0 ? (
            <div className="space-y-2">
              {hubList.map((hub) => (
                <div
                  key={hub.id}
                  className="flex items-center justify-between gap-3 rounded-md border p-3"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <Plane className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{hub.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{hub.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <Input
                        type="number"
                        min={0.5}
                        max={25}
                        step={0.5}
                        defaultValue={Number(hub.radius_miles)}
                        className="w-20"
                        onBlur={(e) => {
                          const radius = parseFloat(e.target.value)
                          if (radius > 0 && radius !== Number(hub.radius_miles)) {
                            updateHubRadiusMutation.mutate({ id: hub.id, radius })
                          }
                        }}
                      />
                      <span className="text-xs text-muted-foreground">mi radius</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeHubMutation.mutate(hub.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground border rounded-lg">
              <Plane className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No hubs configured.</p>
              <p className="text-sm">Search for your airport/hub above. ZIP rates won't apply without one.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ZIP Zones */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>ZIP Zones</CardTitle>
            <CardDescription>Define service areas as sets of ZIP codes.</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Import Rate Card
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setEditingZone(null)
                setZoneDialogOpen(true)
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Zone
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {zones && zones.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>ZIP Codes</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {zones.map((zone) => (
                  <TableRow key={zone.id}>
                    <TableCell>
                      <div
                        className="w-4 h-4 rounded-full border"
                        style={{ backgroundColor: zone.color }}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{zone.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {zone.zip_codes?.length ?? 0} codes
                    </TableCell>
                    <TableCell>
                      <Badge variant={zone.is_active ? 'default' : 'secondary'}>
                        {zone.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingZone(zone)
                            setZoneDialogOpen(true)
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setDeletingZone(zone)
                            setDeleteZoneOpen(true)
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-6 text-muted-foreground border rounded-lg">
              <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No ZIP zones created yet.</p>
              <p className="text-sm">Import a rate card or add a zone manually to get started.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ZIP Rate Rules */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>ZIP Rate Rules</CardTitle>
            <CardDescription>
              Fixed prices for trips between any hub and a ZIP zone.
            </CardDescription>
          </div>
          <Button
            size="sm"
            onClick={() => {
              setEditingRule(null)
              setRuleDialogOpen(true)
            }}
            disabled={!zones || zones.length < 1}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Rule
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {noHubs && (
            <p className="flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <Plane className="h-4 w-4 flex-shrink-0" />
              Add a hub above — ZIP rate rules have no effect until at least one hub is configured.
            </p>
          )}
          {rules && rules.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>ZIP Zone</TableHead>
                  <TableHead>Flat Rate</TableHead>
                  <TableHead>Vehicles</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell className="font-medium">{rule.name}</TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: rule.zip_zone?.color }}
                        />
                        <span>{rule.zip_zone?.name}</span>
                        <ArrowLeftRight className="h-3 w-3 mx-1 text-muted-foreground" />
                        <span className="text-muted-foreground">Hub</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{formatPrice(rule.flat_rate)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {getVehicleCountText(rule.vehicle_ids)}
                    </TableCell>
                    <TableCell>{rule.priority}</TableCell>
                    <TableCell>
                      <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingRule(rule)
                            setRuleDialogOpen(true)
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setDeletingRule(rule)
                            setDeleteRuleOpen(true)
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-6 text-muted-foreground border rounded-lg">
              <ArrowLeftRight className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No ZIP rate rules configured.</p>
              <p className="text-sm">
                {zones && zones.length > 0
                  ? 'Add a rule to price trips between a hub and your ZIP zones.'
                  : 'Create at least one ZIP zone first.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      {activeAccount && (
        <ZipZoneDialog
          open={zoneDialogOpen}
          onOpenChange={(open) => {
            setZoneDialogOpen(open)
            if (!open) setEditingZone(null)
          }}
          zone={editingZone}
          accountId={activeAccount.id}
        />
      )}

      {activeAccount && (
        <ZipRuleDialog
          open={ruleDialogOpen}
          onOpenChange={(open) => {
            setRuleDialogOpen(open)
            if (!open) setEditingRule(null)
          }}
          rule={editingRule}
          zones={zones || []}
          vehicles={vehicles || []}
          accountId={activeAccount.id}
        />
      )}

      {activeAccount && (
        <ZipRateImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          accountId={activeAccount.id}
          vehicles={vehicles || []}
        />
      )}

      <AlertDialog open={deleteZoneOpen} onOpenChange={setDeleteZoneOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete ZIP Zone</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{deletingZone?.name}"? This also deletes all rules that use this zone.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingZone && deleteZoneMutation.mutate(deletingZone.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteZoneMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteRuleOpen} onOpenChange={setDeleteRuleOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{deletingRule?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingRule && deleteRuleMutation.mutate(deletingRule.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteRuleMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
